import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { useSelectedOrganizationId } from "@/organization/api";
import { useOrganizationUsers, useUpdateMembershipRole, useDeleteMembership } from "@/user/api";
import { ThaloDataTable } from "@/components/thalo-data-table";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { OrganizationMembership, OrganizationRole, hasOrgPermission } from "@/user/user";
import { useUser } from "@/api/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2, Key } from "lucide-react";
import { Link } from "@tanstack/react-router";

const UsersPageTableColumns = (
  updateRole: (membershipId: number, role: string) => void,
  deleteMembership: (membershipId: number, userName: string) => void,
  currentUserRole: OrganizationRole | null
): ColumnDef<OrganizationMembership>[] => [
  {
    id: "name",
    accessorFn: (row) => `${row.user.first_name} ${row.user.last_name}`,
    enableSorting: true,
    header: "Name",
    cell: ({ row }) => {
      return (
        <div className="flex flex-col">
          <div className="font-medium">
            {row.original.user.first_name} {row.original.user.last_name}
          </div>
          <div className="text-muted-foreground text-sm">
            {row.original.user.username}
          </div>
        </div>
      );
    },
  },
  {
    id: "email",
    accessorKey: "user.email",
    enableSorting: true,
    header: "Email",
    cell: ({ row }) => row.original.user.email,
  },
  {
    id: "role",
    accessorKey: "role",
    enableSorting: true,
    header: "Role",
    cell: ({ row }) => {
      const role = row.original.role;
      const isAdmin = currentUserRole === OrganizationRole.ADMIN;

      if (!isAdmin) {
        return (
          <div className="capitalize">
            {role}
          </div>
        );
      }

      return (
        <Select
          value={role}
          onValueChange={(newRole) => updateRole(row.original.id, newRole)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={OrganizationRole.ADMIN}>Admin</SelectItem>
            <SelectItem value={OrganizationRole.VIEWER}>Viewer</SelectItem>
            <SelectItem value={OrganizationRole.INSTALLER}>Installer</SelectItem>
          </SelectContent>
        </Select>
      );
    },
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      const isAdmin = currentUserRole === OrganizationRole.ADMIN;

      if (!isAdmin) {
        return null;
      }

      const userName = `${row.original.user.first_name} ${row.original.user.last_name}`;

      return (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="icon">
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove user from organization?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to remove {userName} from this organization?
                You'll have to invite them again to undo this.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteMembership(row.original.id, userName)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      );
    },
  },
];

const UsersPage = () => {
  const organizationId = useSelectedOrganizationId();
  const [sorting, setSorting] = useState<SortingState>([]);
  const { data, isLoading } = useOrganizationUsers(organizationId, sorting);
  const { data: currentUser } = useUser();
  const updateMembershipRole = useUpdateMembershipRole();
  const deleteMembership = useDeleteMembership();

  // Find current user's role in the selected organization
  const currentUserRole = data?.results?.find(
    (membership: OrganizationMembership) => membership.user.id === currentUser?.id
  )?.role || null;

  const handleRoleUpdate = (membershipId: number, role: string) => {
    updateMembershipRole.mutate({ membershipId, organizationId, role });
  };

  const handleDeleteMembership = (membershipId: number, userName: string) => {
    deleteMembership.mutate({ membershipId, organizationId });
  };

  // Check if user can manage invite codes (admins only)
  const canManageInviteCodes = currentUser && hasOrgPermission(
    currentUser,
    organizationId,
    'sensorkit_meta.change_organization'
  );

  return (
    <Card className="">
      <div className="flex items-center justify-between p-3 lg:p-4">
        <h2 className="font-semibold text-lg">Users</h2>
        {canManageInviteCodes && (
          <Link
            to="/organization/$organizationId/invite-codes"
            params={{ organizationId }}
          >
            <Button variant="outline">
              <Key className="h-4 w-4 mr-2" />
              Invite Codes
            </Button>
          </Link>
        )}
      </div>
      <div className="col-span-12 lg:col-span-12">
        <ThaloDataTable
          variant="thalo"
          data={data?.results || []}
          columns={UsersPageTableColumns(handleRoleUpdate, handleDeleteMembership, currentUserRole)}
          isLoading={isLoading}
          pageCount={1}
          itemsPerPage={100}
          showColumnFilter={false}
          sorting={sorting}
          onSortingChange={(newSorting: SortingState) => {
            setSorting(newSorting);
          }}
        />
      </div>
    </Card>
  );
};

export default UsersPage;
